export const NUMBER_REGEX = /^-?\d+([,.]\d+)?$/;
const negativeLeftSymbol = '\u2212';
const integerSeparator = '\u202F';

const replaceEnUsChars = (value: string) => value.replace(/,/g, integerSeparator).replace(/-/g, negativeLeftSymbol);

const formatter3noSep = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 3, useGrouping: false }
);
const format3noSep = (value: number) => replaceEnUsChars(formatter3noSep.format(value));

const formatter3 = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 3, useGrouping: true }
);
const format3 = (value: number) => replaceEnUsChars(formatter3.format(value));

const formatter0 = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 0, useGrouping: true }
);
const format0 = (value: number) => replaceEnUsChars(formatter0.format(value));

const formatter2 = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 2, useGrouping: true }
);
const format2 = (value: number) => replaceEnUsChars(formatter2.format(value));

const formatter4 = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 4, useGrouping: true }
);
const format4 = (value: number) => replaceEnUsChars(formatter4.format(value));

const formatter2padded = new Intl.NumberFormat(
	'en-US',
	{ maximumFractionDigits: 2, minimumFractionDigits: 2, useGrouping: true }
);
const format2padded = (value: number) => replaceEnUsChars(formatter2padded.format(value));

/**
 * Truncates (does not round) a number to 2 decimals; toFixed removes float noise (1744.3 * 100).
 * @param value - number to truncate
 * @returns the truncated number
 */
export const truncateTwoDecimals = (value: number) => Math.trunc(Number((value * 100).toFixed(6))) / 100;

const formatLengthMm2Deci = (value: number) => format2padded(truncateTwoDecimals(value));

export type NumberFormat = 'thickness' | 'width' | 'length-m' | 'weight' | 'weight-rounded'
	| 'percentage-rounded' | 'percentage' | 'length-mm' | 'amount' | 'generic' | 'weight-with-zero' | 'number'
	| 'weight-aggregate' | 'linearMassDensity' | 'diameter' | 'chemical-percentage' | 'length-mm-2-deci';

function getNumberFormat(format: NumberFormat) {
	switch (format) {
	case 'thickness':
		return format3noSep;
	case 'width':
	case 'length-m':
	case 'weight':
	case 'generic':
	case 'weight-with-zero':
		return format3;
	case 'weight-rounded':
	case 'percentage-rounded':
	case 'number':
		return format0;
	case 'percentage':
	case 'length-mm':
	case 'linearMassDensity':
	case 'diameter':
		return format2;
	case 'amount':
		return format2padded;
	case 'length-mm-2-deci':
		return formatLengthMm2Deci;
	case 'chemical-percentage':
		return format4;
	default:
		throw new Error(`unknown format '${ String(format) }'`);
	}
}

export function formatNumber(value: number | undefined, format: NumberFormat, unit?: string) {
	if (!value && value !== 0) {
		return '';
	}
	let finalFormat = format;
	if (format === 'weight-aggregate') {
		finalFormat = value > 0 && value < 1 ? 'weight' : 'weight-rounded';
	}
	const formattedNumber = getNumberFormat(finalFormat)(value);
	return unit ? `${ formattedNumber }\u00A0${ format !== 'amount' ? unit.toLowerCase() : unit }` : formattedNumber;
}

export function formatHiddenNumber(value: number | string, format: string) {
	if (format === 'length-mm-2-deci') {
		return '';
	}
	const quantity = String(value);
	let zeroToFill;
	let nbZeros;
	switch (format) {
	case 'thickness':
	case 'width':
	case 'length-m':
	case 'weight':
	case 'generic':
	case 'weight-with-zero':
		zeroToFill = '000';
		nbZeros = 3;
		break;
	// case 'length-mm':
	default:
		zeroToFill = '00';
		nbZeros = 2;
		break;
	}
	const decimals = quantity.split('.');
	if (decimals.length < 2) {
		return `.${ zeroToFill }`;
	}
	if (decimals[1].length > nbZeros - 1) {
		return '';
	}
	let zerosFill = '';
	for (let i = decimals[1].length; i < nbZeros; i++) {
		zerosFill = `${ zerosFill }0`;
	}
	return zerosFill;
}
export function formatDimension(
	thickness: number | string,
	width: number | string,
	length?: number | string,
	invalidValue = ''
) {
	const thicknessVal = typeof thickness === 'string' ? Number(thickness) : thickness;
	const widthVal = typeof width === 'string' ? Number(width) : width;
	if (!thicknessVal || !widthVal) {
		return invalidValue;
	}
	const lengthVal = typeof length === 'string' ? Number(length) : length;
	const lengthPart = lengthVal ? ` ×\u00A0${ formatNumber(lengthVal, 'length-mm') }` : '';
	return `${ formatNumber(thicknessVal, 'thickness') } ×\u00A0${ formatNumber(widthVal, 'width') }${ lengthPart }`;
}

export function formatTextDimension(
	thickness: number | string,
	width: number | string,
	length?: number | string,
	invalidValue = ''
) {
	if (!thickness || !width) {
		return invalidValue;
	}
	const lengthPart = length ? ` ×\u00A0${ length }` : '';
	return `${ thickness } ×\u00A0${ width }${ lengthPart }`;
}

export function parseNumberFromText(
	value: string | undefined
) {
	if (!value) {
		return undefined;
	}
	return Number(value.replace(/[^\d.-]+/g, ''));
}

export function isNumber(value?: string) {
	return value !== undefined && NUMBER_REGEX.test(value);
}

export function unformatTextDimension(
	value: string
) {
	if (!value) {
		return undefined;
	}
	const parsed = value.split(/[x×]/).map(v => Number(v.replace(/[^\d.]+/g, '')));
	if (parsed.length !== 2 && parsed.length !== 3) {
		return undefined;
	}
	for (const val of parsed) {
		if (Number.isNaN(val)) {
			return undefined;
		}
	}
	return parsed.length === 2
		? { thickness: parsed[0], width: parsed[1] }
		: { thickness: parsed[0], width: parsed[1], length: parsed[2] };
}
